import React, { useState } from 'react';
import { Copy, CaretDown, Check, ArrowSquareOut } from '@phosphor-icons/react';

import { Button } from '../ui/button';
import { addPopupWidgetUtm } from '../../lib/addUtmParams';
import type {
  SearchResult,
  FeedbackResponse,
  AnswerAttachment,
  AnswerBlock as AnswerBlockType,
  AnswerState,
  RedirectRule,
  Action,
  NextBestAction,
  ActionClick,
  KnowledgeSourceGroup,
} from './types';
import { AnswerBlock } from './AnswerBlock';
import { AnswerLoadingStatus } from './AnswerLoadingStatus';
import { AttachmentThumbnails } from './AttachmentThumbnails';
import { KnowledgeGroupSelector } from './KnowledgeGroupSelector';
import { blocksToPlainText } from './blocks/utils';
import AnswerActions from './AnswerActions';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Feedback } from '@/components/feedback';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface AnswerProps {
  index: number;
  blocks?: AnswerBlockType[];
  state?: AnswerState;
  question: string;
  searchResults?: SearchResult[];
  searchQueryId?: string;
  hideCitations?: boolean;
  feedback?: FeedbackResponse;
  onFeedback?: (response: FeedbackResponse) => void;
  disableFeedback?: boolean;
  textConfig?: {
    positiveAnswerText?: string;
    negativeAnswerText?: string;
    loadingSearchText?: string;
    loadingAnswerText?: string;
    uncertaintyFallbackText?: Record<string, string>;
  };
  searchIntentId?: string;
  redirectRules?: RedirectRule[];
  attachments?: AnswerAttachment[];
  actions?: Action[];
  handleActionClick?: (action: ActionClick) => void;
  nextBestActions?: NextBestAction[];
  isLastAnswer?: boolean;
  onFeedbackReasonSubmit?: (reason: string) => void;
  hasError?: boolean;
  knowledgeSourceGroups?: KnowledgeSourceGroup[];
  onKnowledgeGroupSelect?: (group: KnowledgeSourceGroup) => void;
  statusMessage?: string;
}

export function Answer({
  index,
  blocks = [],
  question,
  searchResults,
  state = 'completed',
  searchQueryId,
  searchIntentId,
  hideCitations = false,
  feedback,
  onFeedback,
  disableFeedback = false,
  textConfig = {},
  redirectRules,
  attachments = [],
  actions,
  handleActionClick,
  nextBestActions,
  isLastAnswer,
  onFeedbackReasonSubmit,
  hasError = false,
  knowledgeSourceGroups,
  onKnowledgeGroupSelect,
  statusMessage,
}: AnswerProps) {
  const [isCopied, setIsCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const answer = blocksToPlainText(blocks);

  const isAwaitingKnowledgeGroup = state === 'awaiting-knowledge-group';
  const isFetching = state === 'fetching-search-results' || state === 'fetching-stream';
  const showLoadingStatus = isFetching && (blocks.length === 1 || Boolean(statusMessage));
  const isFetchingSearchResults = state === 'fetching-search-results';
  const isCompleted = state === 'completed';
  const showError = hasError && isCompleted;
  const loadingAnswerText = statusMessage ?? textConfig.loadingAnswerText;
  const loadingText = isFetchingSearchResults ? textConfig.loadingSearchText : loadingAnswerText;
  const hideBlocksDuringProgress = showLoadingStatus && Boolean(statusMessage);

  const handleCopy = async () => {
    try {
      // Attempt to use the Clipboard API
      await navigator.clipboard.writeText(answer);
    } catch (err) {
      console.warn('Clipboard API failed, using fallback:', err);

      // Fallback for environments where clipboard API is not available
      const textArea = document.createElement('textarea');
      textArea.value = answer;
      textArea.style.position = 'fixed'; // Avoid scrolling to bottom
      textArea.style.opacity = '0'; // Make it invisible
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand('copy');
      } catch (fallbackErr) {
        console.error('Fallback: Oops, unable to copy', fallbackErr);
      }

      document.body.removeChild(textArea);
    }

    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleFeedback = (response: FeedbackResponse) => {
    if (onFeedback && searchQueryId) {
      onFeedback(response);
    }
  };

  return (
    <div
      className="rounded-lg relative mx-auto overflow-hidden bg-card border border-primary"
      data-name="SearchResults"
      data-index={index}
    >
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-start justify-between gap-2 min-h-9">
          <h2 className="leading-normal font-semibold m-0 text-foreground self-center text-lg">{question}</h2>
          {isCompleted && !showError && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => void handleCopy()} className="-mt-1 flex-shrink-0">
                    {isCopied ? (
                      <Check className="text-feedback-positive" weight="bold" />
                    ) : (
                      <Copy className="text-secondary-foreground" weight="bold" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isCopied ? 'Copied!' : 'Copy'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <AttachmentThumbnails
          items={attachments.map((a, i) => ({ id: `${i}`, src: a.url, url: a.url, alt: a.filename }))}
          size="xs"
          className="mt-2"
        />
      </div>

      {showLoadingStatus && (
        <div className="px-4 pt-0 pb-2">
          <AnswerLoadingStatus text={loadingText ?? ''} />
        </div>
      )}

      {isAwaitingKnowledgeGroup && knowledgeSourceGroups && onKnowledgeGroupSelect ? (
        <KnowledgeGroupSelector groups={knowledgeSourceGroups} onGroupSelect={onKnowledgeGroupSelect} />
      ) : (
        <div className="flex flex-col">
          {!hideBlocksDuringProgress &&
            blocks?.map((block, i) => (
              <div
                key={i}
                className={cn(
                  'px-4 pt-0 pb-2 border-b last:border-b-0 border-dashed',
                  showError && 'text-muted-foreground',
                )}
              >
                <AnswerBlock block={block} redirectRules={redirectRules} state={state} />
              </div>
            ))}
        </div>
      )}

      {/* Next Best Actions */}
      {isCompleted && !showError && (
        <AnswerActions
          actions={actions}
          nextBestActions={nextBestActions}
          isLastAnswer={isLastAnswer}
          handleActionClick={handleActionClick}
          searchQueryId={searchQueryId || ''}
          searchIntentId={searchIntentId}
          question={question}
          answer={answer}
        />
      )}

      {isCompleted && !disableFeedback && !showError && (
        <div className="p-4 pt-0">
          <Feedback
            feedback={feedback}
            onFeedback={handleFeedback}
            textConfig={textConfig}
            disabled={disableFeedback}
            feedbackReasonLength={500}
            feedbackReasonPlaceholder="What could we have done better? Your feedback is valuable to us..."
            onFeedbackReasonSubmit={onFeedbackReasonSubmit}
            feedbackReasonThankYouText="Thank you for your feedback!"
          />
        </div>
      )}

      {!hideCitations && searchResults && searchResults.length > 0 && (
        <div className="overflow-hidden bg-card border-dashed border-0 border-t-2">
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded} className="w-full">
            <div>
              <CollapsibleTrigger asChild>
                <Button variant="secondary" className="w-full justify-between h-11" data-name="RelatedSources">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate text-base">Sources</span>
                  </div>
                  <CaretDown
                    className={`size-4 flex-shrink-0 transition-transform duration-200 text-secondary-foreground ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                    weight="bold"
                  />
                  <span className="sr-only">Toggle</span>
                </Button>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent className="divide-y divide-dark-300 overflow-hidden CollapsibleContent">
              {searchResults.map((result) => (
                <a
                  key={result.id}
                  href={addPopupWidgetUtm(result.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 transition-colors duration-200 hover:bg-accent bg-card text-foreground"
                >
                  <span className="flex-grow truncate">{result.title}</span>
                  <ArrowSquareOut className="size-4 flex-shrink-0 text-secondary-foreground" weight="bold" />
                </a>
              ))}
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </div>
  );
}
